import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./TableCommon.css";
import "./MonthlyProfitPage.css";
import { apiGet } from "../utils/apiClient";
import { useUserContext } from "../contexts/UserContext";
import { useMallContext } from "../contexts/MallContext";
import LoadingInline from "../components/LoadingInline";
import { isFreePlanUser } from "../utils/plan";
import { UPGRADE_FORM_URL, notifyUpgradeInterest } from "../components/FreePlanUpgradeDialog";

function isNegative(v: number | string | null | undefined) {
  if (v === null || v === undefined || v === "") return false;
  if (typeof v === "number") return v < 0;

  const s0 = String(v).trim();
  if (!s0) return false;

  if (s0.startsWith("▲")) return true;
  if (/^\(.*\)$/.test(s0)) return true;
  if (/^[\-\u2212\uFF0D\u2010\u2011\u2012\u2013\u2014]/.test(s0)) return true;

  const s = s0
    .replace(/[,\s]/g, "")
    .replace(/[￥¥円]/g, "")
    .replace(/[%％]$/, "");

  const n = Number(s);
  return !Number.isNaN(n) ? n < 0 : false;
}

function fmt(v: number | string | null | undefined) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "string" && v.endsWith("%")) return v;
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("ja-JP");
}

function generateFiscalMonths(fiscalMonth: number, baseYear: number) {
  const months: { ymKey: string; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const offsetMonth = fiscalMonth + i;
    const year = baseYear + Math.floor((offsetMonth - 1) / 12);
    const month = ((offsetMonth - 1) % 12) + 1;

    const ymKey = `${year}-${String(month).padStart(2, "0")}-01`;
    const label = `${year}年${month}月`;
    months.push({ ymKey, label });
  }
  return months;
}

function generateQuarters(labels: string[]) {
  const titles = ["第1四半期", "第2四半期", "第3四半期", "第4四半期"];
  return titles.map((t, i) => ({
    title: t,
    months: labels.slice(i * 3, i * 3 + 3),
  }));
}

function generateQuarters2(labels: string[]) {
  const titles = ["第1四半期", "第2四半期", "第3四半期", "第4四半期"];
  const MONTHS_PER_QUARTER = 6;
  return titles.map((t, i) => ({
    title: t,
    months: labels.slice(
      i * MONTHS_PER_QUARTER,
      i * MONTHS_PER_QUARTER + MONTHS_PER_QUARTER
    ),
  }));
}

const PREV_LAYOUT_LS_KEY = "monthlyProfitPrevLayout";

// 時系列表示用：前期4四半期 → 今期4四半期（各3か月）
function generateQuartersTimeline(prevLabels: string[], currLabels: string[]) {
  return [
    ...generateQuarters(prevLabels).map((q) => ({ ...q, title: `前期 ${q.title}` })),
    ...generateQuarters(currLabels).map((q) => ({ ...q, title: `今期 ${q.title}` })),
  ];
}

function interleave(a: string[], b: string[]) {
  const out: string[] = [];
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i]) out.push(a[i]);
    if (b[i]) out.push(b[i]);
  }
  return out;
}

function fmtRound(v: number | string | null | undefined) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return Math.round(n).toLocaleString("ja-JP");
}

function fmtPct(v: number | string | null | undefined, digits = 1) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "string" && v.trim().endsWith("%")) return v;

  const n = Number(v);
  if (Number.isNaN(n)) return String(v);

  return (n * 100).toFixed(digits) + "%";
}

const MonthlyProfitPage: React.FC = () => {
  const user = useUserContext();
  const { mall } = useMallContext();
  const isAmazonMall = mall === "amazon";
  const isYahooMall = mall === "yahoo";
  // 楽天固有の行（メルマガ・アフィリ・楽天広告内訳）を出さないモール
  const isNonRakutenMall = isAmazonMall || isYahooMall;

  const [pl, setPl] = useState<Record<string, any>>({});
  const [fiscalMonth, setFiscalMonth] = useState<number>(4);
  const [lastYearMonth, setLastYearMonth] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showPrev, setShowPrev] = useState(false);
  // 前期列の並び順：paired = 月ごとに前期・今期が交互（既定）／timeline = 前期12か月 → 今期12か月
  // 選んだ並び順はブラウザに覚えておく（「前期列を表示」のチェックは従来どおり覚えない）
  const [prevLayout, setPrevLayoutState] = useState<"paired" | "timeline">(() => {
    try {
      return localStorage.getItem(PREV_LAYOUT_LS_KEY) === "timeline" ? "timeline" : "paired";
    } catch {
      return "paired";
    }
  });
  const setPrevLayout = (v: "paired" | "timeline") => {
    setPrevLayoutState(v);
    try {
      localStorage.setItem(PREV_LAYOUT_LS_KEY, v);
    } catch {
      // 保存できなくても表示の切り替えはそのまま使える
    }
  };
  const isTimeline = showPrev && prevLayout === "timeline";
  const showTotal = true;

  const [collapsed, setCollapsed] = useState<{
    kpi: boolean;
    expense: boolean;
    promo: boolean;
    ad: boolean;
  }>({
    kpi: false,
    expense: false,
    promo: false,
    ad: false,
  });

  const toggle = (k: keyof typeof collapsed) => {
    setCollapsed((p) => ({ ...p, [k]: !p[k] }));
  };

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const head1Ref = useRef<HTMLTableRowElement | null>(null);
  const head2Ref = useRef<HTMLTableRowElement | null>(null);

  // =========================
  // Free plan: 今月だけ表示
  // =========================
  const isFreePlan = useMemo(() => isFreePlanUser(user), [user]);
  const now = new Date();
  const thisMonthLabel = `${now.getFullYear()}年${now.getMonth() + 1}月`;
  const allowedLabels = new Set([thisMonthLabel]);

  const isBlurredLabel = (label: string) => isFreePlan && !allowedLabels.has(label);

  const monthSepClass = (monthColIndex: number) => {
    // 時系列では、前期と今期の境目だけ太い線にする
    if (isTimeline && monthColIndex === monthLabels2.length) return "pl-term-sep";
    const span = showPrev && !isTimeline ? 6 : 3;
    return monthColIndex !== 0 && monthColIndex % span === 0 ? "pl-qsep-l" : "";
  };

  // =========================
  // 合計用の数値パース & 合計
  // =========================
  function parseNum(v: any): number | null {
    if (v === null || v === undefined || v === "") return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;

    let s = String(v).trim();
    if (!s) return null;

    let sign = 1;

    if (s.startsWith("▲")) {
      sign = -1;
      s = s.slice(1);
    }
    if (/^\(.*\)$/.test(s)) {
      sign = -1;
      s = s.slice(1, -1);
    }
    if (/^[\-\u2212\uFF0D\u2010\u2011\u2012\u2013\u2014]/.test(s)) {
      sign = -1;
      s = s.replace(/^[\-\u2212\uFF0D\u2010\u2011\u2012\u2013\u2014]/, "");
    }

    s = s.replace(/[,\s]/g, "").replace(/[￥¥円]/g, "").replace(/[%％]$/, "");

    const n = Number(s);
    if (Number.isNaN(n)) return null;
    return n * sign;
  }

  function sumVisible(labels: string[], vals: any[], parity?: 0 | 1): number | null {
    let sum = 0;
    let has = false;

    for (let i = 0; i < vals.length; i++) {
      if (parity !== undefined && termOfCol(i) !== parity) continue;

      const label = labels[i];
      if (isBlurredLabel(label)) continue;

      const n = parseNum(vals[i]);
      if (n === null) continue;
      sum += n;
      has = true;
    }
    return has ? sum : null;
  }

  // =========================
  // 表示セル（合計列を先頭に追加）
  // =========================
  const cells = (
    labels: string[],
    vals: (number | string | null | undefined)[],
    opts?: { bold?: boolean }
  ) => {
    const boldStyle = opts?.bold ? ({ fontWeight: 700 } as React.CSSProperties) : undefined;

    const monthTds = labels.map((label, i) => {
      const blurred = isBlurredLabel(label);
      const neg = isNegative(vals[i]);
      const cls = [blurred ? "mp-blur" : "", neg ? "dp-neg" : ""].filter(Boolean).join(" ");

      return (
        <td key={i} className={monthSepClass(i)}>
          <span className={cls} style={boldStyle}>
            {fmt(vals[i] ?? "")}
          </span>
        </td>
      );
    });

    if (!showTotal) return monthTds;

    if (showPrev) {
      const prevTotal = sumVisible(labels, vals, 0);
      const currTotal = sumVisible(labels, vals, 1);

      const prevCls = [prevTotal !== null && prevTotal < 0 ? "dp-neg" : ""].filter(Boolean).join(" ");
      const currCls = [currTotal !== null && currTotal < 0 ? "dp-neg" : ""].filter(Boolean).join(" ");

      const totalTds = [
        <td key="total-prev" className="pl-qsep-l">
          <span className={prevCls} style={boldStyle}>
            {prevTotal === null ? "" : fmt(prevTotal)}
          </span>
        </td>,
        <td key="total-curr" className="pl-qsep-r">
          <span className={currCls} style={boldStyle}>
            {currTotal === null ? "" : fmt(currTotal)}
          </span>
        </td>,
      ];

      return [...totalTds, ...monthTds];
    } else {
      const total = sumVisible(labels, vals);
      const cls = [total !== null && total < 0 ? "dp-neg" : ""].filter(Boolean).join(" ");

      const totalTd = (
        <td key="total" className="pl-qsep-l pl-qsep-r">
          <span className={cls} style={boldStyle}>
            {total === null ? "" : fmt(total)}
          </span>
        </td>
      );

      return [totalTd, ...monthTds];
    }
  };

  const cellsRound = (
    labels: string[],
    vals: (number | string | null | undefined)[],
    // calcTotal を渡すと、合計列は月の値の足し算ではなくこの計算を使う
    opts?: { bold?: boolean; calcTotal?: (parity?: 0 | 1) => number | null }
  ) => {
    const boldStyle = opts?.bold ? ({ fontWeight: 700 } as React.CSSProperties) : undefined;
    const totalOf = (parity?: 0 | 1) =>
      opts?.calcTotal ? opts.calcTotal(parity) : sumVisible(labels, vals, parity);

    const monthTds = labels.map((label, i) => {
      const blurred = isBlurredLabel(label);
      const neg = isNegative(vals[i]);
      const cls = [blurred ? "mp-blur" : "", neg ? "dp-neg" : ""].filter(Boolean).join(" ");

      return (
        <td key={i} className={monthSepClass(i)}>
          <span className={cls} style={boldStyle}>
            {fmtRound(vals[i] ?? "")}
          </span>
        </td>
      );
    });

    if (!showTotal) return monthTds;

    if (showPrev) {
      const prevTotal = totalOf(0);
      const currTotal = totalOf(1);

      const prevCls = [prevTotal !== null && prevTotal < 0 ? "dp-neg" : ""].filter(Boolean).join(" ");
      const currCls = [currTotal !== null && currTotal < 0 ? "dp-neg" : ""].filter(Boolean).join(" ");

      const totalTds = [
        <td key="total-prev" className="pl-qsep-l">
          <span className={prevCls} style={boldStyle}>
            {prevTotal === null ? "" : fmtRound(prevTotal)}
          </span>
        </td>,
        <td key="total-curr" className="pl-qsep-r">
          <span className={currCls} style={boldStyle}>
            {currTotal === null ? "" : fmtRound(currTotal)}
          </span>
        </td>,
      ];

      return [...totalTds, ...monthTds];
    } else {
      const total = totalOf();
      const cls = [total !== null && total < 0 ? "dp-neg" : ""].filter(Boolean).join(" ");

      const totalTd = (
        <td key="total" className="pl-qsep-l pl-qsep-r">
          <span className={cls} style={boldStyle}>
            {total === null ? "" : fmtRound(total)}
          </span>
        </td>
      );

      return [totalTd, ...monthTds];
    }
  };

  // ✅ pct は totalFrom を渡したときだけ「合計％」も出す
  // ✅ 正の％は緑、負の％は赤に固定
  const cellsPct = (
    labels: string[],
    vals: (number | string | null | undefined)[],
    digits = 1,
    opts?: {
      bold?: boolean;
      totalFrom?: {
        numer: (number | string | null | undefined)[];
        denom: (number | string | null | undefined)[];
      };
    }
  ) => {
    const getPctStyle = (neg: boolean): React.CSSProperties => ({
      color: neg ? "#d32f2f" : "#16a34a",
      fontWeight: opts?.bold ? 700 : neg ? 600 : 800,
    });

    const monthTds = labels.map((label, i) => {
      const blurred = isBlurredLabel(label);
      const text = fmtPct(vals[i] ?? "", digits);
      const neg = isNegative(text);
      const cls = [blurred ? "mp-blur" : "", neg ? "dp-neg" : ""].filter(Boolean).join(" ");

      return (
        <td key={i} className={monthSepClass(i)}>
          <span className={cls} style={getPctStyle(neg)}>
            {text}
          </span>
        </td>
      );
    });

    if (!showTotal) return monthTds;

    const calcTotalPct = (parity?: 0 | 1): string => {
      const tf = opts?.totalFrom;
      if (!tf) return "";

      const numerSum = sumVisible(labels, tf.numer, parity);
      const denomSum = sumVisible(labels, tf.denom, parity);
      if (numerSum === null || denomSum === null || denomSum === 0) return "";

      return fmtPct(numerSum / denomSum, digits);
    };

    if (showPrev) {
      const prevTxt = calcTotalPct(0);
      const currTxt = calcTotalPct(1);
      const prevNeg = isNegative(prevTxt);
      const currNeg = isNegative(currTxt);

      return [
        <td key="total-prev" className="pl-qsep-l">
          <span style={prevTxt ? getPctStyle(prevNeg) : undefined}>{prevTxt}</span>
        </td>,
        <td key="total-curr" className="pl-qsep-r">
          <span style={currTxt ? getPctStyle(currNeg) : undefined}>{currTxt}</span>
        </td>,
        ...monthTds,
      ];
    } else {
      const totalTxt = calcTotalPct();
      const totalNeg = isNegative(totalTxt);

      return [
        <td key="total" className="pl-qsep-l pl-qsep-r">
          <span style={totalTxt ? getPctStyle(totalNeg) : undefined}>{totalTxt}</span>
        </td>,
        ...monthTds,
      ];
    }
  };

  // ---- PL データ読み込み ----
  useEffect(() => {
    if (user.loading) return;
    if (!user.companyId && !user.superAdmin) return;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await apiGet("/api/dashboard/monthly");
        if (!res.ok) {
          // 失敗時に無言で空表を出さない（原因不明の問い合わせにつながるため）
          throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();

        const fiscal = json.fiscalMonth ?? 4;
        setFiscalMonth(fiscal);

        const rows = json.data ?? [];
        if (!Array.isArray(rows)) return;

        if (rows.length > 0) {
          const maxYm = rows
            .map((r: any) => String(r?.year_month || ""))
            .filter(Boolean)
            .sort()
            .slice(-1)[0];
          if (maxYm) setLastYearMonth(maxYm);
        }

        const map: Record<string, any> = {};
        rows.forEach((r: any) => {
          const y = r.year_month.slice(0, 4);
          const m = Number(r.year_month.slice(5, 7));
          const key = `${y}年${m}月`;
          map[key] = r;
        });

        setPl(map);
      } catch (e) {
        console.error("[MonthlyProfitPage] 月次損益の取得に失敗", e);
        setPl({});
        setLoadError("損益データを取得できませんでした。時間をおいて再度お試しください。");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [user]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const apply = () => {
      const h1 = head1Ref.current?.offsetHeight ?? 0;
      const h2 = head2Ref.current?.offsetHeight ?? 0;
      el.style.setProperty("--pl-head1", `${h1}px`);
      el.style.setProperty("--pl-head2", `${h2}px`);
    };

    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, [loading, showPrev, prevLayout]);

  // ============================
  // 月リスト生成
  // ============================
  let baseYear = new Date().getFullYear();
  let baseYear2 = new Date().getFullYear() - 1;

  if (lastYearMonth) {
    const y = Number(lastYearMonth.slice(0, 4));
    const m = Number(lastYearMonth.slice(5, 7));
    baseYear = m >= fiscalMonth ? y : y - 1;
  }

  baseYear2 = baseYear - 1;

  const fiscalMonths = generateFiscalMonths(fiscalMonth, baseYear);
  const monthLabels = fiscalMonths.map((m) => m.label);
  const quarters = generateQuarters(monthLabels);

  const fiscalMonths2 = generateFiscalMonths(fiscalMonth, baseYear2);
  const monthLabels2 = fiscalMonths2.map((m) => m.label);

  const pairedLabels = interleave(monthLabels2, monthLabels);
  const quarters2 = generateQuarters2(pairedLabels);

  const timelineLabels = [...monthLabels2, ...monthLabels];
  const quartersTimeline = generateQuartersTimeline(monthLabels2, monthLabels);

  // 前期表示中の列の並び（合計・CSV の「前期／今期」の判定もこの並びに従う）
  const compareLabels = isTimeline ? timelineLabels : pairedLabels;

  // 前期表示中、i 列目が前期(0)か今期(1)か。交互表示では i % 2 と同じ
  function termOfCol(i: number): 0 | 1 {
    if (isTimeline) return i < monthLabels2.length ? 0 : 1;
    return i % 2 === 0 ? 0 : 1;
  }

  // ============================
  // データ取得関数
  // ============================
  const val = (key: string): any[] => monthLabels.map((label) => pl[label]?.[key] ?? "");

  const hasUsableValue = (v: any): boolean => {
    if (v === null || v === undefined) return false;
    const s = String(v).trim();
    return s !== "";
  };

  const sumKeysOrBlank = (row: any, keys: string[]): number | "" => {
    if (!row) return "";
    const hasAny = keys.some((k) => hasUsableValue(row[k]));
    if (!hasAny) return "";
    return keys.reduce((acc, k) => acc + (Number(row[k]) || 0), 0);
  };

  const valSum = (keys: string[]): any[] =>
    monthLabels.map((label) => {
      const row = pl[label];
      return sumKeysOrBlank(row, keys);
    });

  const valRateFromSum = (keys: string[]): any[] =>
    monthLabels.map((label) => {
      const row = pl[label];
      if (!row) return "";
      const sum = sumKeysOrBlank(row, keys);
      if (sum === "") return "";
      const sales = Number(row.sales) || 0;
      if (!sales) return "";
      return ((sum / sales) * 100).toFixed(1) + "%";
    });

  const valForPaired = (key: string): any[] => compareLabels.map((label) => pl[label]?.[key] ?? "");

  const valSumForPaired = (keys: string[]): any[] =>
    compareLabels.map((label) => {
      const row = pl[label];
      return sumKeysOrBlank(row, keys);
    });

  const valRateForPaired = (keys: string[]): any[] =>
    compareLabels.map((label) => {
      const row = pl[label];
      if (!row) return "";
      const sum = sumKeysOrBlank(row, keys);
      if (sum === "") return "";
      const sales = Number(row.sales) || 0;
      if (!sales) return "";
      return ((sum / sales) * 100).toFixed(1) + "%";
    });

  // ============================
  // 前期切替用
  // ============================
  const tableLabels = showPrev ? compareLabels : monthLabels;
  const tableQuarters = showPrev ? (isTimeline ? quartersTimeline : quarters2) : quarters;

  const getVal = (key: string): any[] => (showPrev ? valForPaired(key) : val(key));
  const getSum = (keys: string[]): any[] => (showPrev ? valSumForPaired(keys) : valSum(keys));
  const getRateFromSum = (keys: string[]): any[] =>
    showPrev ? valRateForPaired(keys) : valRateFromSum(keys);

  // ============================
  // ✅ 営業利益：null対策で補完（fixed_cost null は 0 扱い）
  // ============================
  const getOperatingProfitVals = (): any[] =>
    tableLabels.map((label) => {
      const row = pl[label];
      if (!row) return "";

      const op = row.operating_profit;
      if (op !== null && op !== undefined && op !== "") return op;

      const hasMarginal = hasUsableValue(row.marginal_profit);
      const hasFixed = hasUsableValue(row.fixed_cost);
      if (!hasMarginal && !hasFixed) return "";

      const mp = Number(row.marginal_profit);
      const fc = row.fixed_cost === null || row.fixed_cost === undefined ? 0 : Number(row.fixed_cost);

      if (!Number.isFinite(mp)) return "";
      if (!Number.isFinite(fc)) return "";
      return mp - fc;
    });

  const getOperatingProfitRateVals = (): any[] =>
    tableLabels.map((label, idx) => {
      const row = pl[label];
      if (!row) return "";

      const r = row.operating_profit_rate;
      if (r !== null && r !== undefined && r !== "") return r;

      const hasMarginal = hasUsableValue(row.marginal_profit);
      const hasFixed = hasUsableValue(row.fixed_cost);
      if (!hasMarginal && !hasFixed) return "";

      const sales = Number(row.sales);
      const op = Number(getOperatingProfitVals()[idx]);

      if (!Number.isFinite(sales) || sales === 0) return "";
      if (!Number.isFinite(op)) return "";
      return op / sales; // 0..1
    });

  // ============================
  // 損益分岐点売上高（楽天のみ）＝ 固定費 ÷ 限界利益率（月次メールと同じ式）
  // 固定費が未入力・限界利益率が0以下・データ無しの月は空欄
  // ============================
  const getBreakEvenSalesVals = (): any[] =>
    tableLabels.map((label) => {
      const row = pl[label];
      if (!row) return "";
      if (!hasUsableValue(row.fixed_cost) || !hasUsableValue(row.marginal_profit_rate)) return "";

      const fc = Number(row.fixed_cost);
      const rate = Number(row.marginal_profit_rate);
      if (!Number.isFinite(fc) || !Number.isFinite(rate) || rate <= 0) return "";
      return fc / rate;
    });

  // 合計列：月の値は足さず、固定費合計 ÷（限界利益合計 ÷ 売上合計）
  const calcBreakEvenSalesTotal = (parity?: 0 | 1): number | null => {
    const fixedSum = sumVisible(tableLabels, getVal("fixed_cost"), parity);
    const marginalSum = sumVisible(tableLabels, getVal("marginal_profit"), parity);
    const salesSum = sumVisible(tableLabels, getVal("sales"), parity);
    if (fixedSum === null || marginalSum === null || salesSum === null || salesSum === 0) return null;

    const rate = marginalSum / salesSum;
    if (rate <= 0) return null;
    return fixedSum / rate;
  };

  // ============================
  // 楽天外広告費 表示用キー
  // ============================
  const NON_RAKUTEN_AD_COST_KEYS = [
    "non_rakuten_ad_1_cost",
    "non_rakuten_ad_2_cost",
    "non_rakuten_ad_3_cost",
  ];

  // 販促費＝楽天の販促費合計＋楽天外広告費（金額・比率で必ず同じ集合を使う）
  const PROMO_TOTAL_KEYS = ["total_advertising", ...NON_RAKUTEN_AD_COST_KEYS];

  // 広告費＝楽天の広告費合計＋楽天外広告費（金額・比率で必ず同じ集合を使う）
  const AD_TOTAL_KEYS = ["total_promotion", ...NON_RAKUTEN_AD_COST_KEYS];

  // ============================
  // rowSpan 動的
  // ============================
  const kpiRowSpan = collapsed.kpi ? 1 : 7;

  const nonRakutenAdRowCount =
    !collapsed.expense && !collapsed.promo && !collapsed.ad
      ? [1, 2, 3].filter((i) =>
          tableLabels.some((l) => pl[l]?.[`non_rakuten_ad_${i}_name`])
        ).length
      : 0;

  // 基準の 27 行は「楽天の広告内訳9行」を含む。折りたたみ時は9行減、
  // Amazon 展開時はスポンサー広告の内訳6行なので差分の3行を減らす
  const expenseRowSpan = collapsed.expense
    ? 2
    : 27
    + nonRakutenAdRowCount
    - (collapsed.promo ? 17 : 0)
    - (!collapsed.promo && collapsed.ad ? 9 : 0)
    - (!collapsed.promo && !collapsed.ad && isAmazonMall ? 3 : 0)
    // Yahoo は広告内訳行なし（請求明細集計のため）
    - (!collapsed.promo && !collapsed.ad && isYahooMall ? 9 : 0)
    // Amazon/Yahoo はメルマガ・アフィリエイト（各2行）を出さない
    - (!collapsed.promo && isNonRakutenMall ? 4 : 0);

  // ============================
  // CSV（あなたのまま）
  // ============================
  function csvEscape(v: any) {
    const s = v === null || v === undefined ? "" : String(v);
    if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  function fmtForCsv(kind: "text" | "round" | "pct", v: any, labelForMask: string, digits = 1) {
    if (isBlurredLabel(labelForMask)) return "";
    if (kind === "round") return fmtRound(v);
    if (kind === "pct") return fmtPct(v, digits);
    return fmt(v);
  }

  function totalCellsForCsv(
    kind: "text" | "round" | "pct",
    labels: string[],
    values: any[],
    calcTotal?: (parity?: 0 | 1) => number | null
  ): string[] {
    if (!showTotal) return [];
    if (kind === "pct") return showPrev ? ["", ""] : [""];
    const totalOf = (parity?: 0 | 1) => (calcTotal ? calcTotal(parity) : sumVisible(labels, values, parity));
    if (showPrev) {
      const prevTotal = totalOf(0);
      const currTotal = totalOf(1);
      const f = kind === "round" ? fmtRound : fmt;
      return [prevTotal === null ? "" : f(prevTotal), currTotal === null ? "" : f(currTotal)];
    } else {
      const total = totalOf();
      const f = kind === "round" ? fmtRound : fmt;
      return [total === null ? "" : f(total)];
    }
  }

  function buildCsvString() {
    const termRowMonths = tableLabels.map((_, idx) =>
      showPrev ? (termOfCol(idx) === 0 ? "前期" : "今期") : "今期"
    );

    const termRow = showTotal
      ? showPrev
        ? ["前期", "今期", ...termRowMonths]
        : ["今期", ...termRowMonths]
      : termRowMonths;

    const monthRow = showTotal
      ? showPrev
        ? ["合計", "合計", ...tableLabels]
        : ["合計", ...tableLabels]
      : tableLabels;

    const lines: string[][] = [];
    lines.push(["区分", "項目", ...termRow]);
    lines.push(["", "", ...monthRow]);

    type CsvRow = {
      group: string;
      label: string;
      kind: "text" | "round" | "pct";
      values: any[];
      digits?: number;
      calcTotal?: (parity?: 0 | 1) => number | null;
    };

    const rows: CsvRow[] = [
      { group: "主要指標", label: "売上（クーポン値引き後）＋送料込", kind: "round", values: getVal("sales") },
      { group: "主要指標", label: "クーポン値引額", kind: "round", values: getVal("coupon_shop") },
      { group: "主要指標", label: "クーポン比率", kind: "pct", values: getVal("coupon_rate"), digits: 1 },
      { group: "主要指標", label: "件数", kind: "round", values: getVal("order_count") },
      { group: "主要指標", label: "アクセス", kind: "round", values: getVal("visit") },
      { group: "主要指標", label: "転換率", kind: "pct", values: getVal("cvr"), digits: 1 },
      { group: "主要指標", label: "客単価", kind: "round", values: getVal("unit_price") },

      { group: "変動費", label: "費用合計", kind: "round", values: getVal("total_expense") },
      { group: "変動費", label: "比率", kind: "pct", values: getVal("total_expense_rate"), digits: 1 },

      { group: "変動費", label: "原価額", kind: "round", values: getVal("cost_amount") },
      { group: "変動費", label: "原価 比率", kind: "pct", values: getVal("cost_amount_rate"), digits: 1 },

      { group: "変動費", label: "販売手数料＋決済手数料", kind: "round", values: getVal("fee") },
      { group: "変動費", label: "販売手数料＋決済手数料 比率", kind: "pct", values: getVal("fee_rate"), digits: 1 },

      { group: "変動費", label: "物流費", kind: "round", values: getVal("logistics_cost") },
      { group: "変動費", label: "物流費 比率", kind: "pct", values: getVal("logistics_cost_rate"), digits: 1 },

      { group: "変動費", label: "販促費", kind: "round", values: getSum(PROMO_TOTAL_KEYS) },
      { group: "変動費", label: "販促費 比率", kind: "pct", values: getRateFromSum(PROMO_TOTAL_KEYS), digits: 1 },

      { group: "変動費", label: "ポイント費用", kind: "round", values: getVal("point_amount_spent") },
      { group: "変動費", label: "ポイント費用 比率", kind: "pct", values: getVal("point_amount_spent_rate"), digits: 1 },

      { group: "変動費", label: "メルマガ費用", kind: "round", values: getVal("mail_amount_spent") },
      { group: "変動費", label: "メルマガ費用 比率", kind: "pct", values: getVal("mail_amount_spent_rate"), digits: 1 },

      { group: "変動費", label: "アフィリエイト", kind: "round", values: getVal("afl_amount_spent") },
      { group: "変動費", label: "アフィリエイト 比率", kind: "pct", values: getVal("afl_amount_spent_rate"), digits: 1 },

      { group: "変動費", label: "広告費", kind: "round", values: getSum(AD_TOTAL_KEYS) },
      { group: "変動費", label: "広告費 比率", kind: "pct", values: getRateFromSum(AD_TOTAL_KEYS), digits: 1 },

      ...(isYahooMall
        ? [] // Yahoo は広告内訳なし（広告費合計のみ）
        : !isAmazonMall
        ? [
          { group: "変動費", label: "楽天広告", kind: "round" as const, values: getVal("ec_amount_spent") },
          { group: "変動費", label: "RPP広告", kind: "round" as const, values: getVal("rpp_amount_spent") },
          { group: "変動費", label: "RPP広告 比率", kind: "pct" as const, values: getVal("rpp_amount_spent_rate"), digits: 1 },
          { group: "変動費", label: "RPP-EXP広告", kind: "round" as const, values: getVal("rppexp_amount_spent") },
          { group: "変動費", label: "楽天CPA広告", kind: "round" as const, values: getVal("cpa_amount_spent") },
          { group: "変動費", label: "クーポンアドバンス広告", kind: "round" as const, values: getVal("cpnadv_amount_spent") },
          { group: "変動費", label: "TDA広告", kind: "round" as const, values: getVal("tda_amount_spent") },
          { group: "変動費", label: "TDA-EXP広告", kind: "round" as const, values: getVal("tdaexp_amount_spent") },
          { group: "変動費", label: "DEAL成果報酬", kind: "round" as const, values: getVal("deal_amount_spent") },
        ]
        : [
          // Amazon はスポンサー広告の内訳（rpp/rppexp/cpnadv キーにマップ済み）
          { group: "変動費", label: "スポンサープロダクト", kind: "round" as const, values: getVal("rpp_amount_spent") },
          { group: "変動費", label: "スポンサープロダクト 比率", kind: "pct" as const, values: getVal("rpp_amount_spent_rate"), digits: 1 },
          { group: "変動費", label: "スポンサーブランド", kind: "round" as const, values: getVal("rppexp_amount_spent") },
          { group: "変動費", label: "スポンサーブランド 比率", kind: "pct" as const, values: getVal("rppexp_amount_spent_rate"), digits: 1 },
          { group: "変動費", label: "スポンサーディスプレイ", kind: "round" as const, values: getVal("cpnadv_amount_spent") },
          { group: "変動費", label: "スポンサーディスプレイ 比率", kind: "pct" as const, values: getVal("cpnadv_amount_spent_rate"), digits: 1 },
        ]),

      ...[1, 2, 3].flatMap((i) => {
        const nameKey = `non_rakuten_ad_${i}_name`;
        const costKey = `non_rakuten_ad_${i}_cost`;
        const adLabel =
          tableLabels.map((l) => pl[l]?.[nameKey]).find(Boolean) || `楽天外広告${i}`;
        const hasData = tableLabels.some((l) => pl[l]?.[nameKey]);
        if (!hasData) return [];
        return [
          { group: "変動費", label: adLabel as string, kind: "round" as const, values: getVal(costKey) },
        ];
      }),

      { group: "限界利益", label: "限界利益額", kind: "round", values: getVal("marginal_profit") },
      { group: "限界利益", label: "限界利益 比率", kind: "pct", values: getVal("marginal_profit_rate"), digits: 1 },

      { group: "固定費", label: "固定費", kind: "round", values: getVal("fixed_cost") },

      { group: "利益", label: "営業利益", kind: "round", values: getOperatingProfitVals() },
      { group: "利益", label: "営業利益 比率", kind: "pct", values: getOperatingProfitRateVals(), digits: 1 },

      ...(isNonRakutenMall
        ? []
        : [
          {
            group: "利益",
            label: "損益分岐点売上高",
            kind: "round" as const,
            values: getBreakEvenSalesVals(),
            calcTotal: calcBreakEvenSalesTotal,
          },
        ]),
    ];

    for (const r of rows) {
      const totals = totalCellsForCsv(r.kind, tableLabels, r.values, r.calcTotal);
      const monthVals = r.values.map((v, i) => fmtForCsv(r.kind, v, tableLabels[i], r.digits ?? 1));
      lines.push([r.group, r.label, ...totals, ...monthVals]);
    }

    return "\uFEFF" + lines.map((row) => row.map(csvEscape).join(",")).join("\r\n");
  }

  function downloadCsv() {
    const csv = buildCsvString();
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const mode = showPrev ? (isTimeline ? "compare_timeline" : "compare") : "this";
    const filename = `monthly_pl_${mode}_${y}${m}${dd}.csv`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  const onToggleKey = (fn: () => void) => (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fn();
    }
  };

  const titleLabel =
    mall === "amazon"
      ? "Amazon月次損益計算表（PL）"
      : mall === "yahoo"
        ? "Yahoo!ショッピング月次損益計算表（PL）"
        : "楽天月次損益計算表（PL）";

  // ===============================
  // 画面生成
  // ===============================
  return (
    <div className="w-pl flex flex-col relative">
      {loading && (
        <div className="fixed top-14 bottom-0 left-0 md:left-[210px] right-0 z-50 flex items-center justify-center bg-white/0">
          <LoadingInline />
        </div>
      )}

      {!loading && loadError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {loadError}
        </div>
      )}

      <div
        style={{
          marginTop: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 4,
          }}
        >
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <label
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              className="text-sm"
            >
              <input
                type="checkbox"
                checked={showPrev}
                onChange={(e) => setShowPrev(e.target.checked)}
                disabled={loading}
              />
              前期列を表示
            </label>

            {showPrev && (
              <div
                role="radiogroup"
                aria-label="前期列の並び順"
                style={{ display: "flex", gap: 12, alignItems: "center", whiteSpace: "nowrap" }}
                className="text-sm"
              >
                <span className="text-slate-500">並び順：</span>
                <label style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="prevLayout"
                    checked={prevLayout === "paired"}
                    onChange={() => setPrevLayout("paired")}
                    disabled={loading}
                  />
                  月ごとに交互
                </label>
                <label style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="prevLayout"
                    checked={prevLayout === "timeline"}
                    onChange={() => setPrevLayout("timeline")}
                    disabled={loading}
                  />
                  前期→今期の時系列
                </label>
              </div>
            )}
          </div>

          {isFreePlan && (
            <p className="text-xs text-slate-500">
              フリープランは当月のみ表示されます。過去データの閲覧は
              <a
                href={UPGRADE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
                onClick={() => notifyUpgradeInterest("月次利益（過去データ表示）")}
              >
                プランをアップグレード
              </a>
              してください。
            </p>
          )}
        </div>

        <button
          type="button"
          className="text-sm border rounded px-2 py-1 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={downloadCsv}
          style={{ whiteSpace: "nowrap" }}
          disabled={loading}
        >
          CSVエクスポート
        </button>
      </div>

      <p className="mt-4 mb-1 text-[11px] text-slate-400">
        ← 横にスクロールすると他の月を表示できます →
      </p>

      <div ref={wrapRef} className="pl-table-wrapper pl-table-vert" style={{ marginTop: "4px" }}>
        <table className="pl-table">
          <thead>
            <tr ref={head1Ref} className="pl-headrow-1">
              <th rowSpan={2} colSpan={2} className="title-cell dp-title-sticky">
                {titleLabel}
                <br />
                前期／今期比較
              </th>

              {showTotal && (
                <th colSpan={showPrev ? 2 : 1} className="quarter-header pl-qsep-l pl-qsep-r">
                  合計
                </th>
              )}

              {tableQuarters.map((q, qi) => (
                <th
                  key={q.title}
                  colSpan={q.months.length}
                  className={`quarter-header ${
                    qi === 0 ? "" : isTimeline && qi === 4 ? "pl-term-sep" : "pl-qsep-l"
                  }`}
                >
                  {q.title}
                </th>
              ))}
            </tr>

            <tr ref={head2Ref} className="pl-headrow-2">
              {showTotal &&
                (showPrev ? (
                  <>
                    <th className="month-header pl-qsep-l">合計</th>
                    <th className="month-header pl-qsep-r">合計</th>
                  </>
                ) : (
                  <th className="month-header pl-qsep-l pl-qsep-r">合計</th>
                ))}

              {tableLabels.map((m, idx) => (
                <th key={m} className={`month-header ${monthSepClass(idx)}`}>
                  {m}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* ===== 主要指標 ===== */}
            <tr className="pl-gsep-top">
              <td rowSpan={kpiRowSpan} className="group-col">
                主要指標
              </td>
              <td
                className="label-col pl-fold-toggle"
                role="button"
                tabIndex={0}
                onClick={() => toggle("kpi")}
                onKeyDown={onToggleKey(() => toggle("kpi"))}
                title="クリックで主要指標を折りたたみ"
              >
                <span className="pl-fold-caret">{collapsed.kpi ? "▶" : "▼"}</span>
                売上（クーポン値引き後）＋送料込
              </td>
              {cellsRound(tableLabels, getVal("sales"))}
            </tr>

            {!collapsed.kpi && (
              <>
                <tr>
                  <td className="label-col indent-1">クーポン値引額</td>
                  {cellsRound(tableLabels, getVal("coupon_shop"))}
                </tr>

                <tr>
                  <td className="label-col indent-1 rate-label">クーポン比率</td>
                  {cellsPct(tableLabels, getVal("coupon_rate"), 1, {
                    totalFrom: { numer: getVal("coupon_shop"), denom: getVal("sales") },
                  })}
                </tr>

                <tr>
                  <td className="label-col indent-1">件数</td>
                  {cellsRound(tableLabels, getVal("order_count"))}
                </tr>
                <tr>
                  <td className="label-col indent-1">アクセス</td>
                  {cellsRound(tableLabels, getVal("visit"))}
                </tr>
                <tr>
                  <td className="label-col indent-1 rate-label">転換率</td>
                  {cellsPct(tableLabels, getVal("cvr"), 1, {
                    totalFrom: { numer: getVal("order_count"), denom: getVal("visit") },
                  })}
                </tr>
                <tr>
                  <td className="label-col indent-1">客単価</td>
                  {cellsRound(tableLabels, getVal("unit_price"))}
                </tr>
              </>
            )}

            {/* ===== 変動費 ===== */}
            <tr className="pl-gsep-top">
              <td rowSpan={expenseRowSpan} className="group-col">
                変動費
              </td>
              <td
                className="label-col pl-fold-toggle"
                role="button"
                tabIndex={0}
                onClick={() => toggle("expense")}
                onKeyDown={onToggleKey(() => toggle("expense"))}
                title="クリックで変動費の内訳を折りたたみ"
              >
                <span className="pl-fold-caret">{collapsed.expense ? "▶" : "▼"}</span>
                変動費合計
              </td>
              {cellsRound(tableLabels, getVal("total_expense"))}
            </tr>

            <tr>
              <td className="label-col rate-label">比率</td>
              {cellsPct(tableLabels, getVal("total_expense_rate"), 1, {
                totalFrom: { numer: getVal("total_expense"), denom: getVal("sales") },
              })}
            </tr>

            {!collapsed.expense && (
              <>
                <tr>
                  <td className="label-col indent-1">原価額</td>
                  {cellsRound(tableLabels, getVal("cost_amount"), { bold: true })}
                </tr>
                <tr>
                  <td className="label-col indent-1 rate-label">比率</td>
                  {cellsPct(tableLabels, getVal("cost_amount_rate"), 1, {
                    bold: true,
                    totalFrom: { numer: getVal("cost_amount"), denom: getVal("sales") },
                  })}
                </tr>

                <tr>
                  <td className="label-col indent-1">販売手数料＋決済手数料</td>
                  {cellsRound(tableLabels, getVal("fee"))}
                </tr>
                <tr>
                  <td className="label-col indent-1 rate-label">比率</td>
                  {cellsPct(tableLabels, getVal("fee_rate"), 1, {
                    totalFrom: { numer: getVal("fee"), denom: getVal("sales") },
                  })}
                </tr>

                <tr>
                  <td className="label-col indent-1">物流費</td>
                  {cellsRound(tableLabels, getVal("logistics_cost"))}
                </tr>
                <tr>
                  <td className="label-col indent-1 rate-label">比率</td>
                  {cellsPct(tableLabels, getVal("logistics_cost_rate"), 1, {
                    totalFrom: { numer: getVal("logistics_cost"), denom: getVal("sales") },
                  })}
                </tr>

                <tr>
                  <td
                    className="label-col indent-1 pl-fold-toggle"
                    role="button"
                    tabIndex={0}
                    onClick={() => toggle("promo")}
                    onKeyDown={onToggleKey(() => toggle("promo"))}
                    title="クリックで内訳を折りたたみ"
                  >
                    <span>{collapsed.promo ? "▶" : "▼"}</span>
                    販促費
                  </td>
                  {cellsRound(tableLabels, getSum(PROMO_TOTAL_KEYS), { bold: true })}
                </tr>
                <tr>
                  <td className="label-col indent-1 rate-label">比率</td>
                  {cellsPct(tableLabels, getRateFromSum(PROMO_TOTAL_KEYS), 1, {
                    bold: true,
                    totalFrom: { numer: getSum(PROMO_TOTAL_KEYS), denom: getVal("sales") },
                  })}
                </tr>

                {!collapsed.promo && (
                  <>
                    <tr>
                      <td className="label-col indent-2">ポイント費用</td>
                      {cellsRound(tableLabels, getVal("point_amount_spent"))}
                    </tr>
                    <tr>
                      <td className="label-col indent-2 rate-label">比率</td>
                      {cellsPct(tableLabels, getVal("point_amount_spent_rate"), 1, {
                        totalFrom: { numer: getVal("point_amount_spent"), denom: getVal("sales") },
                      })}
                    </tr>

                    {/* メルマガ・アフィリエイトは楽天固有。Amazon/Yahoo では常に0のため出さない */}
                    {!isNonRakutenMall && (
                      <>
                        <tr>
                          <td className="label-col indent-2">メルマガ費用</td>
                          {cellsRound(tableLabels, getVal("mail_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-2 rate-label">比率</td>
                          {cellsPct(tableLabels, getVal("mail_amount_spent_rate"), 1, {
                            totalFrom: { numer: getVal("mail_amount_spent"), denom: getVal("sales") },
                          })}
                        </tr>
                      </>
                    )}

                    {!isNonRakutenMall && (
                      <>
                        <tr>
                          <td className="label-col indent-2">アフィリエイト</td>
                          {cellsRound(tableLabels, getVal("afl_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-2 rate-label">比率</td>
                          {cellsPct(tableLabels, getVal("afl_amount_spent_rate"), 1, {
                            totalFrom: { numer: getVal("afl_amount_spent"), denom: getVal("sales") },
                          })}
                        </tr>
                      </>
                    )}

                    <tr>
                      {/* Amazon もスポンサー広告の内訳を持つため折りたたみを出す */}
                      <td
                        className="label-col indent-2 pl-fold-toggle"
                        role="button"
                        tabIndex={0}
                        onClick={() => toggle("ad")}
                        onKeyDown={onToggleKey(() => toggle("ad"))}
                        title="クリックで広告内訳を折りたたみ"
                      >
                        <span>{collapsed.ad ? "▶" : "▼"}</span>
                        広告費
                      </td>
                      {cellsRound(tableLabels, getSum(AD_TOTAL_KEYS))}
                    </tr>
                    <tr>
                      <td className="label-col indent-2 rate-label">比率</td>
                      {cellsPct(tableLabels, getRateFromSum(AD_TOTAL_KEYS), 1, {
                        totalFrom: { numer: getSum(AD_TOTAL_KEYS), denom: getVal("sales") },
                      })}
                    </tr>

                    {/* Amazon: スポンサー広告の内訳（API側で ads_sp/sb/sd を rpp/rppexp/cpnadv にマップ） */}
                    {isAmazonMall && !collapsed.ad && (
                      <>
                        <tr>
                          <td className="label-col indent-3">スポンサープロダクト</td>
                          {cellsRound(tableLabels, getVal("rpp_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3 rate-label">比率</td>
                          {cellsPct(tableLabels, getVal("rpp_amount_spent_rate"), 1, {
                            totalFrom: { numer: getVal("rpp_amount_spent"), denom: getVal("sales") },
                          })}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">スポンサーブランド</td>
                          {cellsRound(tableLabels, getVal("rppexp_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3 rate-label">比率</td>
                          {cellsPct(tableLabels, getVal("rppexp_amount_spent_rate"), 1, {
                            totalFrom: { numer: getVal("rppexp_amount_spent"), denom: getVal("sales") },
                          })}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">スポンサーディスプレイ</td>
                          {cellsRound(tableLabels, getVal("cpnadv_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3 rate-label">比率</td>
                          {cellsPct(tableLabels, getVal("cpnadv_amount_spent_rate"), 1, {
                            totalFrom: { numer: getVal("cpnadv_amount_spent"), denom: getVal("sales") },
                          })}
                        </tr>
                      </>
                    )}

                    {!isNonRakutenMall && !collapsed.ad && (
                      <>
                        <tr>
                          <td className="label-col indent-3">楽天広告</td>
                          {cellsRound(tableLabels, getVal("ec_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">RPP広告</td>
                          {cellsRound(tableLabels, getVal("rpp_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3 rate-label">比率</td>
                          {cellsPct(tableLabels, getVal("rpp_amount_spent_rate"), 1, {
                            totalFrom: { numer: getVal("rpp_amount_spent"), denom: getVal("sales") },
                          })}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">RPP-EXP広告</td>
                          {cellsRound(tableLabels, getVal("rppexp_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">楽天CPA広告</td>
                          {cellsRound(tableLabels, getVal("cpa_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">クーポンアドバンス広告</td>
                          {cellsRound(tableLabels, getVal("cpnadv_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">TDA広告</td>
                          {cellsRound(tableLabels, getVal("tda_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">TDA-EXP広告</td>
                          {cellsRound(tableLabels, getVal("tdaexp_amount_spent"))}
                        </tr>
                        <tr>
                          <td className="label-col indent-3">DEAL成果報酬</td>
                          {cellsRound(tableLabels, getVal("deal_amount_spent"))}
                        </tr>
                        {[1, 2, 3].map((i) => {
                          const nameKey = `non_rakuten_ad_${i}_name`;
                          const costKey = `non_rakuten_ad_${i}_cost`;
                          const hasData = tableLabels.some((l) => pl[l]?.[nameKey]);
                          if (!hasData) return null;
                          const adLabel =
                            tableLabels.map((l) => pl[l]?.[nameKey]).find(Boolean) ||
                            `楽天外広告${i}`;
                          return (
                            <tr key={`non_rakuten_ad_${i}`}>
                              <td className="label-col indent-3">{adLabel}</td>
                              {cellsRound(tableLabels, getVal(costKey))}
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </>
                )}
              </>
            )}

            <tr className="pl-gsep-top">
              <td rowSpan={2} className="group-col">
                限界利益
              </td>
              <td className="label-col">限界利益額</td>
              {cellsRound(tableLabels, getVal("marginal_profit"), { bold: true })}
            </tr>
            <tr>
              <td className="label-col rate-label">比率</td>
              {cellsPct(tableLabels, getVal("marginal_profit_rate"), 1, {
                bold: true,
                totalFrom: { numer: getVal("marginal_profit"), denom: getVal("sales") },
              })}
            </tr>

            <tr className="pl-gsep-top">
              <td className="group-col">固定費</td>
              <td className="label-col">固定費</td>
              {cellsRound(tableLabels, getVal("fixed_cost"))}
            </tr>

            <tr className="pl-gsep-top">
              <td rowSpan={isNonRakutenMall ? 2 : 3} className="group-col">
                利益
              </td>
              <td className="label-col">営業利益</td>
              {cellsRound(tableLabels, getOperatingProfitVals())}
            </tr>
            <tr>
              <td className="label-col rate-label">比率</td>
              {cellsPct(tableLabels, getOperatingProfitRateVals(), 1, {
                totalFrom: { numer: getOperatingProfitVals(), denom: getVal("sales") },
              })}
            </tr>
            {!isNonRakutenMall && (
              <tr>
                <td className="label-col">
                  損益分岐点売上高
                  <span className="block text-[11px] leading-tight text-slate-500">（固定費 ÷ 限界利益率）</span>
                </td>
                {cellsRound(tableLabels, getBreakEvenSalesVals(), { calcTotal: calcBreakEvenSalesTotal })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MonthlyProfitPage;